import { useCallback, useEffect, useRef, useState } from 'react';

import api from '@/services/api';

import {
  type QueuedFeedback,
  getAllQueuedFeedback,
  updateQueuedFeedback,
  deleteQueuedFeedback,
} from '../lib/offlineDb';

const BACKOFF_MS = [30_000, 120_000, 600_000, 1_800_000]; // 30s, 2min, 10min, 30min
const MAX_RETRIES = 10;

export function useFeedbackQueue() {
  const [queueCount, setQueueCount] = useState(0);
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refreshCount = useCallback(async () => {
    const items = await getAllQueuedFeedback();
    setQueueCount(items.filter(i => i.status === 'QUEUED' || i.status === 'FAILED').length);
  }, []);

  const flushOne = useCallback(async (item: QueuedFeedback) => {
    console.info(
      `[FeedbackQueue] Flushing ${item.id} (attempt ${item.retryCount + 1}, category=${item.category})`,
    );
    await updateQueuedFeedback({ ...item, status: 'SENDING' });

    try {
      const formData = new FormData();
      formData.append(
        'metadata',
        JSON.stringify({
          boothId: item.boothId,
          sessionId: item.sessionId,
          category: item.category,
          comment: item.comment,
          locale: item.locale,
          source: item.source,
          flowStep: item.flowStep,
          lastMessage: item.lastMessage,
          appVersion: item.appVersion,
          userEmail: item.userEmail,
          userPhone: item.userPhone,
          healthSnapshot: item.healthSnapshot,
          clientId: item.id,
        }),
      );

      for (let i = 0; i < item.audioClips.length; i++) {
        const clip = item.audioClips[i];
        formData.append(`audio${i}`, clip.blob, `clip-${i}.webm`);
      }

      await api.post('/booth-feedback', formData, {
        headers: { 'Content-Type': 'multipart/form-data', 'X-Idempotency-Key': item.id },
      });

      await deleteQueuedFeedback(item.id);
      console.info(`[FeedbackQueue] Flush OK — ${item.id}`);
    } catch (err) {
      const nextRetry = item.retryCount + 1;
      console.warn(
        `[FeedbackQueue] Flush FAILED — ${item.id} (attempt ${nextRetry}/${MAX_RETRIES})`,
        err,
      );
      if (nextRetry >= MAX_RETRIES) {
        await updateQueuedFeedback({ ...item, status: 'FAILED', retryCount: nextRetry });
      } else {
        await updateQueuedFeedback({ ...item, status: 'QUEUED', retryCount: nextRetry });
      }
    }
  }, []);

  const flush = useCallback(async () => {
    const items = await getAllQueuedFeedback();
    const pending = items
      .filter(i => i.status === 'QUEUED')
      .sort((a, b) => a.createdAt - b.createdAt);

    if (pending.length > 0) {
      console.info(`[FeedbackQueue] Flushing ${pending.length} queued feedback(s)`);
    }

    for (const item of pending) {
      const backoff = BACKOFF_MS[Math.min(item.retryCount, BACKOFF_MS.length - 1)];
      if (Date.now() - item.updatedAt < backoff) continue;
      await flushOne(item);
    }

    await refreshCount();
  }, [flushOne, refreshCount]);

  // Flush on online event
  useEffect(() => {
    const handleOnline = () => {
      console.info('[FeedbackQueue] Back online — triggering flush');
      flush();
    };
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [flush]);

  // Periodic flush check every 30s
  useEffect(() => {
    flushTimerRef.current = setInterval(() => {
      if (navigator.onLine) flush();
    }, 30_000);

    return () => {
      if (flushTimerRef.current) clearInterval(flushTimerRef.current);
    };
  }, [flush]);

  // Initial count
  useEffect(() => {
    refreshCount();
  }, [refreshCount]);

  return { queueCount, flush, refreshCount };
}
